import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { DraftIssue } from "@/lib/draftValidation";

const COLORS: Record<DraftIssue["severity"], string> = {
  required: "bg-red-500",
  recommended: "bg-amber-400",
  warning: "bg-amber-400",
};

export function FieldFlag({
  issue,
  pulse = false,
}: {
  issue: DraftIssue | null | undefined;
  pulse?: boolean;
}) {
  if (!issue) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-block w-2 h-2 rounded-full ${COLORS[issue.severity]} ${pulse ? "animate-pulse" : ""} ml-1.5 align-middle`}
            aria-label={issue.label}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {issue.label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function IssuesBadge({
  required,
  recommended,
  warnings,
}: {
  required: DraftIssue[];
  recommended: DraftIssue[];
  warnings: DraftIssue[];
}) {
  const total = required.length + recommended.length + warnings.length;
  if (total === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Clean
      </span>
    );
  }
  const dominant = required.length > 0 ? "required" : "recommended";
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-help ${
              dominant === "required"
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${COLORS[dominant]}`} />
            {total} issue{total > 1 ? "s" : ""}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm space-y-2 text-xs">
          {required.length > 0 && (
            <div className="space-y-1">
              <div className="font-medium text-red-700">Required missing</div>
              {required.map((issue) => (
                <div key={`required-${issue.field}`} className="text-slate-700">
                  {issue.label}
                </div>
              ))}
            </div>
          )}
          {recommended.length > 0 && (
            <div className="space-y-1">
              <div className="font-medium text-amber-700">Recommended missing</div>
              {recommended.map((issue) => (
                <div key={`recommended-${issue.field}`} className="text-slate-700">
                  {issue.label}
                </div>
              ))}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="space-y-1">
              <div className="font-medium text-amber-700">Warnings</div>
              {warnings.map((issue, index) => (
                <div
                  key={`warning-${issue.field}-${index}`}
                  className="text-slate-700"
                >
                  {issue.label}
                </div>
              ))}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
